import type { AmplitudeTrackPayload } from '@kimuno/api-sdk/lib/types/requests/Track';
import type { SubscriptionResponse } from '@kimuno/api-sdk/lib/types/responses/Billing';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { amplitudeContentTypes, amplitudeEvents, readPageTime } from '../../constants';
import { selectMindItem } from '../../reducers/mindItems.selectors';
import { getSubscription, selectUser } from '../../reducers/user.selectors';
import services from '../../services';
import { getContentEventPayload, getCurrentSearchId } from '../../utils/ampli';
import useRouter from '../../utils/useRouter';
import { useTabVisibility } from './useTabVisibility';

export const usePageReadTracking = (mindItemId: string, currentPage: number, isLoaded: boolean) => {
	const timeoutRef = useRef<number>();
	const isForegroundTab = useTabVisibility();
	const user = useSelector(selectUser);
	const subscription: SubscriptionResponse | undefined = useSelector(getSubscription);
	const {
		match: {
			location: { query },
		},
	} = useRouter();
	const mindItem = useSelector((state) => selectMindItem(state, mindItemId));

	// Refs hold the latest values so the timeout callback reads fresh data
	// without restarting the 30s timer on every state change.
	const userRef = useRef(user);
	const mindItemRef = useRef(mindItem);
	const subscriptionRef = useRef(subscription);
	const currentPageRef = useRef(currentPage);
	const queryRef = useRef(query);
	const hasTrackedRef = useRef(false);

	userRef.current = user;
	mindItemRef.current = mindItem;
	subscriptionRef.current = subscription;
	currentPageRef.current = currentPage;
	queryRef.current = query;

	// Reset tracking flag when the document or page changes
	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		hasTrackedRef.current = false;
	}, [mindItemId, currentPage]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current);
		}
		if (isForegroundTab && isLoaded && !hasTrackedRef.current) {
			timeoutRef.current = window.setTimeout(() => {
				const mindItem = mindItemRef.current;
				if (!userRef.current || !mindItem || !subscriptionRef.current) return;

				const searchId = getCurrentSearchId() || queryRef.current?.search_id;

				const readEventPayload: AmplitudeTrackPayload | null = getContentEventPayload({
					content: mindItem,
					currentPage: currentPageRef.current,
					event_type: amplitudeEvents.PageRead,
					contentType: mindItem.isReadOnly
						? amplitudeContentTypes.readOnlyDocument
						: amplitudeContentTypes.document,
					searchId,
				});

				if (readEventPayload) {
					services.track.ampliServerSideEventTrack(false, readEventPayload);
					hasTrackedRef.current = true;
				}
			}, readPageTime);
		}
		return () => window.clearTimeout(timeoutRef.current);
	}, [mindItemId, currentPage, isForegroundTab, isLoaded]);
};
